import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth/useAuth';
import { useWorkspace } from '../contexts/workspace/useWorkspace';
import { WorkspaceAccessLevel } from '../types';

/**
 * Props for the ProtectedRoute component
 * @interface ProtectedRouteProps
 */
interface ProtectedRouteProps {
  /** Child components to render if the user is authenticated and meets the access requirements */
  children: React.ReactNode;
  /** Whether workspace selection is required */
  requireWorkspace?: boolean;
  /** Whether read/write access is required */
  requireWriteAccess?: boolean;
}

/**
 * ProtectedRoute component
 * @function ProtectedRoute
 * @param {ProtectedRouteProps} props - The props for the component
 * @returns JSX element or redirects based on authentication and access requirements
 */
export default function ProtectedRoute({ 
  children, 
  requireWorkspace = false,
  requireWriteAccess = false 
}: ProtectedRouteProps) {
  /** Auth context data */
  const { isAuthenticated, user } = useAuth();
  /** Workspace context data */
  const { currentWorkspace, currentUserAccess } = useWorkspace();
  /** Current location object from react-router-dom */
  const location = useLocation();

  /**
   * Redirects to login page if the user is not authenticated
   * Preserves the full location including search parameters
   */
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location, search: location.search }} replace />;
  }

  /**
   * Redirects to change password page if account is scheduled for deletion
   */
  if (user?.scheduled_deletion && location.pathname !== '/delete-account') {
    return <Navigate to="/delete-account" replace />;
  }

  /**
   * Redirects to delete account if user is on plans page but deletion scheduled
   */
  if (user?.scheduled_deletion && location.pathname === '/plans') {
    return <Navigate to="/delete-account" replace />;
  }

  /**
   * Redirects to workspace selection if workspace access is required but not available
   */
  if (requireWorkspace && !currentWorkspace) {
    return <Navigate to="/workspaces" state={{ from: location }} replace />;
  }

  /**
   * Redirects to home if read/write access is required but user only has read access
   */
  if (requireWriteAccess && currentUserAccess !== WorkspaceAccessLevel.MEMBER) {
    return <Navigate to="/" replace />;
  }

  /** Returns the child components if the user is authenticated and meets the access requirements */
  return <>{children}</>;
}
